import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import MetricCard from './components/MetricCard';
import ActivityItem from './components/ActivityItem';
import ScheduledPostCard from './components/ScheduledPostCard';
import GrowthChart from './components/GrowthChart';
import QuickActionButton from './components/QuickActionButton';
import { dashboardService } from '../../services/dashboardService';
import { scheduledPostService } from '../../services/scheduledPostService';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';


const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [dateRange, setDateRange] = useState('7days');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Real data from Supabase
  const [metrics, setMetrics] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [growthData, setGrowthData] = useState([]);

  // Load dashboard data
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    loadDashboardData();
  }, [user, authLoading, navigate]);

  // Update growth data when date range changes
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    loadGrowthData();
  }, [dateRange, user, authLoading]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all dashboard data in parallel
      const [metricsData, activityData, postsData, growthChartData] = await Promise.all([
        dashboardService?.getMetrics()?.catch(err => {
          console.error('Metrics error:', err);
          return null;
        }),
        dashboardService?.getRecentActivity(4)?.catch(err => {
          console.error('Activity error:', err);
          return [];
        }),
        scheduledPostService?.getAll(null, null)?.catch(err => {
          console.error('Posts error:', err);
          return [];
        }),
        dashboardService?.getGrowthData(null, getDaysFromRange(dateRange))?.catch(err => {
          console.error('Growth data error:', err);
          return [];
        })
      ]);

      setMetrics(metricsData);
      setRecentActivities(activityData || []);
      // Show only next 4 scheduled posts
      setScheduledPosts(postsData?.slice(0, 4) || []);
      setGrowthData(growthChartData || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadGrowthData = async () => {
    try {
      const days = getDaysFromRange(dateRange);
      const data = await dashboardService?.getGrowthData(null, days)?.catch(err => {
        console.error('Growth data error:', err);
        return [];
      });
      setGrowthData(data || []);
    } catch (err) {
      console.error('Growth data error:', err);
    }
  };

  const getDaysFromRange = (range) => {
    switch (range) {
      case '7days': return 7;
      case '30days': return 30;
      case '90days': return 90;
      default: return 7;
    }
  };

  const metricsData = metrics ? [
    {
      title: "Total Followers",
      value: metrics?.totalFollowers?.toLocaleString(),
      change: `${metrics?.followerChange >= 0 ? '+' : ''}${metrics?.followerChange?.toFixed(1)}%`,
      changeType: metrics?.followerChange >= 0 ? "positive" : "negative",
      icon: "Users",
      iconBg: "bg-gradient-to-br from-primary to-accent"
    },
    {
      title: "Engagement Rate",
      value: `${metrics?.engagementRate?.toFixed(1)}%`,
      change: `${metrics?.engagementChange >= 0 ? '+' : ''}${metrics?.engagementChange?.toFixed(1)}%`,
      changeType: metrics?.engagementChange >= 0 ? "positive" : "negative",
      icon: "TrendingUp",
      iconBg: "bg-gradient-to-br from-accent to-success"
    },
    {
      title: "Scheduled Posts",
      value: metrics?.scheduledPosts?.toString(),
      change: `${metrics?.scheduledChange >= 0 ? '+' : ''}${metrics?.scheduledChange}`,
      changeType: metrics?.scheduledChange >= 0 ? "positive" : "negative",
      icon: "Calendar",
      iconBg: "bg-gradient-to-br from-warning to-primary"
    },
    {
      title: "Total Comments",
      value: metrics?.totalComments?.toString() || '0',
      change: `${metrics?.commentChange >= 0 ? '+' : ''}${metrics?.commentChange || 0}`,
      changeType: metrics?.commentChange >= 0 ? "positive" : "negative",
      icon: "MessageSquare",
      iconBg: "bg-gradient-to-br from-secondary to-primary"
    }
  ] : [];

  const dateRangeOptions = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' }
  ];

  const handleEditPost = (postId) => {
    navigate('/content-scheduler', { state: { editPostId: postId } });
  };

  const handleDeletePost = async (postId) => {
    try {
      await scheduledPostService?.delete(postId);
      // Reload posts
      const postsData = await scheduledPostService?.getAll(null, null);
      setScheduledPosts(postsData?.slice(0, 4) || []);
    } catch (err) {
      console.error('Delete post error:', err);
      setError(err?.message || 'Failed to delete post');
    }
  };

  const handleCreatePost = () => {
    navigate('/content-scheduler');
  };

  const handleScheduleComment = () => {
    navigate('/comment-automation');
  };

  const handleManageBrands = () => {
    navigate('/brand-management');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
          <p className="text-gray-600">Manage your brands, create content, and engage with your audience.</p>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-6 flex items-start gap-3">
            <svg className="w-5 h-5 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-medium">Error loading dashboard</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metricsData?.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Quick Actions</h2>
            <p className="text-sm text-gray-600">Streamline your workflow with these core actions</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <QuickActionButton
              icon="Briefcase"
              label="Manage Brands"
              description="Create and manage your brands"
              onClick={handleManageBrands}
              variant="primary"
            />
            <QuickActionButton
              icon="PlusCircle"
              label="Create Content"
              description="Schedule new social posts"
              onClick={handleCreatePost}
              variant="accent"
            />
            <QuickActionButton
              icon="MessageSquare"
              label="Automate Comments"
              description="Engage with your audience"
              onClick={handleScheduleComment}
              variant="secondary"
            />
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Growth Chart - Takes 2 columns */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Growth Overview</h2>
                  <p className="text-sm text-gray-600 mt-1">Track your audience growth over time</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Select
                    value={dateRange}
                    onChange={(e) => setDateRange(e?.target?.value)}
                    options={dateRangeOptions}
                    className="min-w-[140px]"
                  />
                </div>
              </div>
              <GrowthChart data={growthData} type="line" />
            </div>
          </div>

          {/* Recent Activity */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
                  <p className="text-sm text-gray-600 mt-1">Latest updates</p>
                </div>
              </div>
              <div className="space-y-4">
                {recentActivities?.length > 0 ? (
                  recentActivities?.map((activity) => (
                    <ActivityItem key={activity?.id} {...activity} />
                  ))
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Icon name="Activity" size={24} color="#9ca3af" />
                    </div>
                    <p className="text-gray-500 text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Scheduled Posts */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Upcoming Posts</h2>
              <p className="text-sm text-gray-600">Your scheduled content</p>
            </div>
            <Button
              onClick={() => navigate('/content-scheduler')}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              View All
              <Icon name="ArrowRight" size={16} />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {scheduledPosts?.length > 0 ? (
              scheduledPosts?.map((post) => (
                <ScheduledPostCard
                  key={post?.id}
                  post={post}
                  onEdit={handleEditPost}
                  onDelete={handleDeletePost}
                />
              ))
            ) : (
              <div className="col-span-full">
                <div className="bg-white rounded-xl p-12 border-2 border-dashed border-gray-300 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Calendar" size={28} color="#2563eb" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No scheduled posts</h3>
                  <p className="text-gray-600 mb-6">Start creating content to see it here</p>
                  <Button onClick={handleCreatePost} className="mx-auto">
                    <Icon name="PlusCircle" size={16} className="mr-2" />
                    Create Your First Post
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;